import { useContext, useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import AppContext from './../contexts/AppContext';
import DataContext from './../contexts/DataContext';
import Layout from './Layout';
import SlideContainer from './../components/SlideContainer';
import SlideProgress from '../components/SlideProgress';
import SlideProgressSunnyBot from '../components/SlideProgressSunnyBot';
import SlideProgressDetailed from '../components/SlideProgressDetailed';
import Copyright from '../components/Copyright';
import PopupMessage from '../components/PopupMessage';
import Button from './../components/Button';
import Input from './../components/Input';
import ConfigContext from '../contexts/ConfigContext';
import states from '../data/states';
import sunnyBotMessages from '../data/sunnyBotMessages';

import OptionsForProperty from '../components/OptionsForProperty';
import OptionsForSunlight from '../components/OptionsForSunlight';
import BillSlider from '../components/BillSlider';
import BillButtons from '../components/BillButtons';
import PropertyAddress from '../components/PropertyAddress';
import NeighborsMap from '../components/NeighborsMap';
import UtilityProvider from '../components/UtilityProvider';
import EmailInput from '../components/EmailInput';
import ContactInformation from '../components/ContactInformation';
import {
  LanderConceptFooter,
  LanderConceptHeader,
} from '../components/LanderConcept';
import QuestionContext from '../components/QuestionContext';
import QuestionContextV2 from '../components/QuestionContextV2';

import { UAParser } from 'ua-parser-js';
import SunnyBot, { SunnyBotBackground } from '../components/SunnyBot';
import iconCheck from './../images/isr_feature_form/green_check.svg';
import PhoneNumberInput from '../components/PhoneNumberInput';
import { useSearchParams } from 'react-router-dom';
import { appendScript, formatPhoneNumber } from './../helpers';

declare const Zuko: any;
declare const visitData: any;

export default function Home({ displayPage }: { displayPage?: boolean }) {
  const { features, testName } = useContext(ConfigContext);
  const {
    currentSlide,
    slides,
    showInitialPopup,
    closeInitialPopup,
    geoData,
    getSlideIndex,
    setLoadedHome,
    setStartedFunnel,
    zukoSlug,
    setSelectedNonFamilyHome,
    updateSelectedOption,
    updateCurrentSlide,
    isShortISRFeatureFormCustomer,
    ISRAppointmentDetails,
    slfApiUrl,
  } = useContext(AppContext);
  const { data, updateData } = useContext(DataContext);
  const [isSingleFamilyHome, setIsSingleFamilyHome] = useState(false);
  const [searchParams] = useSearchParams();
  const currentParams = Object.fromEntries(searchParams.entries());
  const showConsentPage =
    currentParams.consent && currentParams.consent === 'yes';
  const showRentersPage =
    currentParams.renter && currentParams.renter === 'yes';

  useEffect(() => {
    if (showConsentPage || showRentersPage) {
      setIsSingleFamilyHome(false);
      return;
    }

    const isSingleFamilyHome = !data.property
      ? true
      : data.property === 'Single Family Home'
      ? true
      : false;
    setIsSingleFamilyHome(isSingleFamilyHome);
    if (!isSingleFamilyHome) {
      if (
        data.property === 'Condo / Apartment' ||
        data.property === 'Mobile Home'
      ) {
        window.location.href = `/${process.env
          .REACT_APP_ROOT_PATH!}?renter=yes`;
        return;
      } else {
        setSelectedNonFamilyHome(data.property);
      }
    }
  }, [data.property]);

  if (!isSingleFamilyHome) {
    closeInitialPopup();
  }

  const progress = ((currentSlide + 1) / (slides.length + 1)) * 100;

  const popupTitle = 'Power your home with sunshine.';
  const popupMessage =
    'Complete this form and a certified installer will contact you to discuss a solution for your exact energy needs.';

  const showPopup = showInitialPopup && currentSlide === 0;
  const titleRef: any = useRef(null);
  const slidesContainerRef: any = useRef(null);
  const slideTitle =
    features.useElecBillByState && slides[currentSlide].key === 'bill'
      ? `The average monthly electricity bill in ${data.selectedCity} is $${data.elecBillByState}. Is your bill higher or lower than this?`
      : slides[currentSlide].title;

  const getStateName = (stateCode: string): string | null => {
    for (const [key, value] of Object.entries(states)) {
      if (value === stateCode?.toUpperCase()) {
        return key;
      }
    }
    return null;
  };

  const getUtilityName = (): string => {
    const providerSlideIndex = getSlideIndex('provider');
    return slides[providerSlideIndex].selectedValue || data.selectedCity;
  };

  useEffect(() => {
    setStartedFunnel(true);
    setLoadedHome(true);
    let sourceData;
    try {
      sourceData = visitData.get();
    } catch (e: any) {
      sourceData = { source: 'direct', medium: 'none' };
    }

    const { device } = UAParser();
    Zuko.trackForm({
      target: document.querySelector('#root > div'),
      slug: zukoSlug || '',
    })
      .setAttribute('ab-test-variant', testName)
      .setAttribute('traffic-source', sourceData.source)
      .setAttribute('traffic-medium', sourceData.medium)
      .setAttribute(
        'device-model',
        !device.vendor && !device.model
          ? 'Unknown'
          : `${device.vendor ? device.vendor + ' ' : ''}${
              device.model ? device.model : ''
            }`
      )
      .trackEvent(Zuko.FORM_VIEW_EVENT);
  }, []);

  const showAppointmentConfirmation =
    !isShortISRFeatureFormCustomer && ISRAppointmentDetails;

  const [name, setName] = useState(currentParams.name);
  const [nameHaveError, setNameHaveError] = useState(false);
  const [phoneInputValue, setPhoneInputValue] = useState(
    formatPhoneNumber(currentParams.phone)
  );
  const [phoneHaveError, setPhoneHaveError] = useState(false);
  const [consented, setConsented] = useState(false);

  const handleNameInput = (e: any) => {
    const name = e.target.value.trim();
    setName(name);
    const nameArray = name.split(' ');

    if (nameArray.length >= 2) {
      setNameHaveError(false);
    } else {
      setNameHaveError(true);
    }
  };

  const handlePhoneInput = (newValue: any, haveError: boolean) => {
    setPhoneInputValue(newValue);
    setPhoneHaveError(haveError);
  };

  return (
    <div
      className={`${displayPage ? '' : 'hidden'} ${
        slides[currentSlide].key === 'bill' ? 'select-none' : ''
      }`}>
      <Layout
        addPadding={!showAppointmentConfirmation}
        hideClickToCall={showConsentPage || showRentersPage}>
        {features.showSunnyBot && <SunnyBotBackground />}
        {showPopup && (
          <PopupMessage
            title={popupTitle}
            message={popupMessage}
            onClose={() => closeInitialPopup()}
            className="md:hidden mt-10"
          />
        )}
        {isSingleFamilyHome && (
          <div
            className={`${
              features.showLanderConcept ? 'hidden' : 'md:hidden'
            } ${showPopup ? 'mt-[26px]' : 'mt-8'}`}>
            {features.useDetailedProgressBar ? (
              <SlideProgressDetailed percentage={progress} />
            ) : (
              <SlideProgress percentage={progress} />
            )}
          </div>
        )}
        <div
          className={`${
            features.showSunnyBot
              ? 'md:mt-[120px] mt-[115px]'
              : 'md:mt-[60px] mt-[26px]'
          } ${
            features.showQuestionsContextV2
              ? 'flex flex-col md:items-center'
              : ''
          }`}>
          {showPopup && (
            <PopupMessage
              title={popupTitle}
              message={popupMessage}
              onClose={() => closeInitialPopup()}
              className="md-max:hidden"
            />
          )}
          {features.showLanderConcept && <LanderConceptHeader />}
          <SlideContainer
            ref={slidesContainerRef}
            removeMinHeight={showPopup || !isSingleFamilyHome}
            hideMobileHeaderBackground={
              !isSingleFamilyHome || showAppointmentConfirmation
            }
            removeWidths={showAppointmentConfirmation}>
            {showAppointmentConfirmation && (
              <div className="text-left mx-auto w-fit bg-white rounded-xl my-6">
                <div className="flex items-center">
                  <p className="text-green-600 bg-green-200 px-4 py-2 rounded-md flex shrink-0">
                    <img src={iconCheck} />
                    <span>Confirmed!</span>
                  </p>
                  <p className="font-bold text-[18px] ml-3 leading-tight">
                    Your appointment is set with{' '}
                    {ISRAppointmentDetails.userName || (
                      <span className="loader-dots small !ml-[35px]"></span>
                    )}
                  </p>
                </div>
                <hr className="my-5"></hr>
                <p className="my-1">
                  <b>Meeting name: </b>
                  Enphase solar consultation
                </p>
                <p className="my-1 flex">
                  <b>Date & Time: </b>
                  {ISRAppointmentDetails.startTime ? (
                    <span className="mx-1">
                      {DateTime.fromISO(
                        ISRAppointmentDetails.startTime
                      ).toLocaleString({
                        weekday: 'long',
                        month: 'long',
                        day: '2-digit',
                      })}
                      {' @ '}
                      {DateTime.fromISO(
                        ISRAppointmentDetails.startTime
                      ).toLocaleString({
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  ) : (
                    <span className="loader-dots small !ml-[35px]"></span>
                  )}
                </p>
                <p className="my-1 flex">
                  <b>Location: </b> <span className="mx-1">Phone meeting:</span>{' '}
                  {ISRAppointmentDetails.phoneNumber ? (
                    <span>{ISRAppointmentDetails.phoneNumber}</span>
                  ) : (
                    <span className="loader-dots small !ml-[35px]"></span>
                  )}
                </p>
              </div>
            )}
            {!showAppointmentConfirmation && isSingleFamilyHome && (
              <>
                {features.showSunnyBot && (
                  <div className="hidden md:block">
                    <SunnyBot
                      data={sunnyBotMessages.find(
                        (message) => message.key === slides[currentSlide].key
                      )}></SunnyBot>
                    <SlideProgressSunnyBot percentage={progress} />
                  </div>
                )}

                <div
                  className={`${
                    features.showLanderConcept ? '' : 'md-max:hidden'
                  }`}>
                  {features.useDetailedProgressBar ? (
                    <div
                      className={
                        features.showLanderConcept
                          ? 'mt-[-20px] relative z-10'
                          : ''
                      }>
                      <SlideProgressDetailed percentage={progress} />
                    </div>
                  ) : (
                    <SlideProgress percentage={progress} />
                  )}
                </div>

                {features.useLocalizedHeadline &&
                  (data.selectedState || geoData.stateName) && (
                    <div>
                      <p className="text-[22px] md:text-[38px] md:mt-[40px] text-black font-bold leading-[1.2]">
                        {getStateName(data.selectedState) || geoData.stateName}{' '}
                        Homeowners Save Money with Solar.
                      </p>
                      <hr className="my-6 md:my-[30px]" />
                    </div>
                  )}

                {features.useUtilityHeadline && (
                  <div>
                    <p className="text-[22px] md:text-[38px] md:mt-[40px] text-black font-bold leading-[1.2]">
                      {getUtilityName()?.toLowerCase() !== 'not listed'
                        ? getUtilityName()
                        : ''}{' '}
                      Homeowners: See Your Cost Savings Now!
                    </p>
                    <hr className="my-6 md:my-[30px]" />
                  </div>
                )}

                {features.showSunnyBot && (
                  <div className="md:hidden">
                    <SunnyBot
                      data={sunnyBotMessages.find(
                        (message) => message.key === slides[currentSlide].key
                      )}></SunnyBot>
                    <SlideProgressSunnyBot percentage={progress} />
                  </div>
                )}

                <p
                  ref={titleRef}
                  className={`${
                    features.showSunnyBot
                      ? 'mt-8 md:mt-10 text-[18px] md:text-[26px] short:text-[18px]'
                      : 'md:mt-10 text-[24px] md:text-[32px]'
                  } text-black`}>
                  {slideTitle}
                </p>
                {features.showQuestionsContextV2
                  ? null
                  : features.showQuestionsContext &&
                    slides[currentSlide].context && (
                      <QuestionContext
                        title={slides[currentSlide].context.title}
                        content={slides[currentSlide].context.content}
                      />
                    )}
                {!isShortISRFeatureFormCustomer && ISRAppointmentDetails ? (
                  <div className="text-left mx-auto w-fit bg-white p-6 rounded-xl my-6">
                    <div className="flex items-center">
                      <p className="text-green-600 bg-green-200 px-4 py-2 rounded-md flex shrink-0">
                        <img src={iconCheck} />
                        <span>Confirmed!</span>
                      </p>
                      <p className="font-bold text-[18px] ml-3 leading-tight">
                        Your appointment is set with{' '}
                        {ISRAppointmentDetails.userName || (
                          <span className="loader-dots small !ml-[35px]"></span>
                        )}
                      </p>
                    </div>
                    <hr className="my-5"></hr>
                    <p className="my-1">
                      <b>Meeting name: </b>
                      Enphase solar consultation
                    </p>
                    <p className="my-1 flex">
                      <b>Date & Time: </b>
                      {ISRAppointmentDetails.startTime ? (
                        <span className="mx-1">
                          {DateTime.fromISO(
                            ISRAppointmentDetails.startTime
                          ).toLocaleString({
                            weekday: 'long',
                            month: 'long',
                            day: '2-digit',
                          })}
                          {' @ '}
                          {DateTime.fromISO(
                            ISRAppointmentDetails.startTime
                          ).toLocaleString({
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </span>
                      ) : (
                        <span className="loader-dots small !ml-[35px]"></span>
                      )}
                    </p>
                    <p className="my-1 flex">
                      <b>Location: </b>{' '}
                      <span className="mx-1">Phone meeting:</span>{' '}
                      {ISRAppointmentDetails.phoneNumber ? (
                        <span>{ISRAppointmentDetails.phoneNumber}</span>
                      ) : (
                        <span className="loader-dots small !ml-[35px]"></span>
                      )}
                    </p>
                  </div>
                ) : (
                  <div className={showPopup ? `md:mb-[55px]` : ''}>
                    {slides[currentSlide].key === 'property' && (
                      <OptionsForProperty />
                    )}
                    {slides[currentSlide].key === 'sunlight' && (
                      <OptionsForSunlight />
                    )}
                    {slides[currentSlide].key === 'bill' && <BillSlider />}
                    {slides[currentSlide].key === 'billButtons' && (
                      <BillButtons />
                    )}
                    {slides[currentSlide].key === 'addressOwnership' && (
                      <PropertyAddress scrollToRef={slidesContainerRef} />
                    )}
                    {features.showNeighborsMap &&
                      slides[currentSlide].key === 'neighborsMap' && (
                        <NeighborsMap />
                      )}
                    {slides[currentSlide].key === 'provider' && (
                      <UtilityProvider />
                    )}
                    {slides[currentSlide].key === 'emailLink' && <EmailInput />}
                    {slides[currentSlide].key === 'contact' && (
                      <ContactInformation scrollToRef={titleRef} />
                    )}
                  </div>
                )}
              </>
            )}
            {!showAppointmentConfirmation &&
              !isSingleFamilyHome &&
              !showConsentPage &&
              !showRentersPage && (
                <>
                  <p className="text-[18px] md:text-[32px] mb-10 text-black w-full">
                    Sorry, we are unable to service you right now
                  </p>
                  <p className="mb-10 text-base">
                    Our estimator is only set up to provide preliminary
                    estimates and installer information to residents of single
                    family homes.
                  </p>
                  <Button
                    title="Go back to form"
                    action={() => {
                      updateData({ ...data, property: null });
                      updateSelectedOption('property', 'null');
                      updateCurrentSlide(0);
                    }}
                    className="mb-4"
                  />
                </>
              )}
            {showConsentPage && !consented && (
              <>
                <p className="font-bold text-[24px] md:text-[36px]">
                  Consent to be contacted by {currentParams.installer}
                </p>
                <p className="font-bold text-[20px] md:text-[22px] my-8">
                  Contact Information:
                </p>
                <p className="mt-[30px] text-[17px] short:!text-[16px] md:text-[18px]">
                  Full name
                </p>
                <Input
                  name="full_name"
                  placeholder="First and last name"
                  onChange={(e: any) => handleNameInput(e)}
                  className="mt-3 md:mt-4 mb-6 md:mb-6 md:w-[400px]"
                  errorMessage="Please enter first and last name"
                  haveError={nameHaveError}
                  defaultValue={name}
                />

                <PhoneNumberInput
                  label="Phone"
                  value={phoneInputValue}
                  onChange={handlePhoneInput}
                />
                <p className="text-[14px]">
                  * We’re here to
                  help! Each time that you fill out this form, you agree to be
                  contacted by phone call, pre-recorded voice, and/or text message
                  at the telephone or mobile number that you entered above by
                  Enphase Energy, Inc. and by{' '}
                  <a
                    href="/solar-installers/"
                    target="_blank"
                    rel="noreferrer"
                    className="underline">
                    one (1) other company
                  </a>{' '}
                  about solar and energy storage products and services. These
                  calls/messages may be sent using automated telephone technology,
                  even if your telephone or mobile number is currently listed on any
                  state, federal or corporate “Do Not Call” list. Message and data
                  rates may apply. You are not required to give your consent here as
                  a condition of any purchase. I understand the{' '}
                  <a
                    href="https://enphase.com/legal/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                    className="underline">
                    Privacy Policy
                  </a>{' '}
                  and agree to the{' '}
                  <a
                    href="https://enphase.com/legal/terms-of-service"
                    target="_blank"
                    rel="noreferrer"
                    className="underline">
                    Terms and conditions
                  </a>
                  .
                </p>
                <Button
                  title="Yes, I Consent"
                  className="mt-6"
                  disabled={
                    !name || !phoneInputValue || nameHaveError || phoneHaveError
                  }
                  action={() => {
                    const sendData = {
                      name,
                      phone: phoneInputValue.replace(/\D/g, ''),
                      installer: currentParams.installer.replace('/', '_'),
                      date: DateTime.now().toLocaleString({
                        weekday: 'long',
                        month: 'long',
                        day: '2-digit',
                      }),
                      time: DateTime.now().toLocaleString({
                        hour: '2-digit',
                        minute: '2-digit',
                      }),
                    };
                    const metaUrl = `${slfApiUrl}/api/user/update_lead_meta/${
                      currentParams.lid
                    }/consent_${encodeURIComponent(
                      currentParams.installer.replace(
                        new RegExp(/\s|\//, 'g'),
                        '_'
                      )
                    )}/${encodeURIComponent(JSON.stringify(sendData))}`;
                    appendScript(metaUrl);
                    fetch(
                      `${slfApiUrl}/api/user/update_tcpa/${currentParams.lid}/${currentParams.bid}`
                    );
                    setConsented(true);
                  }}></Button>
              </>
            )}
            {showConsentPage && consented && (
              <p className="font-bold text-[20px] my-10">
                Thank you! Your consent has been successfully recorded. Your
                solar concierge can now proceed with booking your appointment
                with {currentParams.installer}.<br />
                <br />
                If you have any questions, please refer to our{' '}
                <a
                  href="https://enphase.com/legal/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  className="underline">
                  Privacy Policy
                </a>
                . You may opt out at any time by replying STOP to our messages.
              </p>
            )}
            {showRentersPage && (
              <>
                <p className="text-[26px] md:text-[34px] my-3">
                  We have the perfect solution for you-<br></br>the IQ PowerPack
                  portable energy system
                </p>
                <p className="text-[21px] my-5">
                  Compact, quiet, and powerful, it keeps your essentials running
                  during outages and lets you charge anywhere with solar power
                  or grid power.<br></br>
                  <br></br>Perfect for renters who need flexibility without
                  compromise.
                </p>
                <Button
                  title="Learn more"
                  className="!w-auto !px-12"
                  action={() =>
                    window.open(
                      'https://enphase.com/homeowners/portable-energy',
                      '_blank'
                    )
                  }></Button>
              </>
            )}
          </SlideContainer>
          {features.showQuestionsContextV2 && slides[currentSlide].context && (
            <QuestionContextV2
              title={slides[currentSlide].context.title}
              content={slides[currentSlide].context.content}
            />
          )}
        </div>
        {features.showLanderConcept && !showConsentPage && !showRentersPage && (
          <LanderConceptFooter />
        )}
        <Copyright />
      </Layout>
    </div>
  );
}
